import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure, Tabs, Tab } from '@nextui-org/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CtyButton, formatTimestamp } from '@circuitry-ai/doc-ui';
import { RootState, getMessageByIndex } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import ExplainerPartsCatalogTable from './explainerPartsCatalogTable';
import ExplainerDetails from './explainerdetails';
import ExplainerAnswer from './explaineranswer';

enum TabKeys {
    QUESTION = 'Question',
    KNOWLEDGE = 'Knowledge',
    RANKING = 'Ranking',
    ANSWER = 'Answer',
}

interface PartsCatalogExplainerData {
    retrieved_contexts?: {
        headers: string[];
        rows: any[][];
    };
    reranked_contexts?: {
        headers: string[];
        rows: any[][];
    };
    question?: {
        rephrased_query?: string;
        metadata?: any[];
        total_knowledge?: number;
        top_results?: number;
    };
    answer?: string;
    cypher_query?: string;
    timestamp?: number;
    time?: string;
}

interface ExplainerPartsCatalogModalProps {
    setViewExplainer: (value: boolean) => void;
    explainerAnswer: PartsCatalogExplainerData;
    msgIndex: number;
    userQuery: string;
    metadataMap?: Record<string, any>;
    advisorResponse?: string;
}

const ExplainerPartsCatalogModal = ({
    setViewExplainer,
    explainerAnswer,
    msgIndex,
    userQuery,
    metadataMap = {},
    advisorResponse = ''
}: ExplainerPartsCatalogModalProps) => {
    const { t } = useTranslation();
    const { isOpen, onOpen } = useDisclosure();

    useEffect(() => {
        onOpen();
    }, []);

    const currentQuestion = useSelector((state: RootState) => getMessageByIndex(state, msgIndex));
    const [currentTab, setCurrentTab] = useState<TabKeys>(TabKeys.QUESTION);
    const currentQuestionAnswerTime = Math.floor(metadataMap?.total_time_taken_seconds ?? 0);

    const questionDetails = {
        query: currentQuestion ?? userQuery,
        metadata: explainerAnswer?.question?.metadata?.[0],
        total_knowledge: explainerAnswer?.question?.total_knowledge,
        top_results: explainerAnswer?.question?.top_results,
        rephrased_query: explainerAnswer?.question?.rephrased_query
    };

    const hasRetrievedContexts = explainerAnswer?.retrieved_contexts?.headers &&
        explainerAnswer?.retrieved_contexts?.rows &&
        explainerAnswer.retrieved_contexts.rows.length > 0;

    const hasRerankedContexts = explainerAnswer?.reranked_contexts?.headers &&
        explainerAnswer?.reranked_contexts?.rows &&
        explainerAnswer.reranked_contexts.rows.length > 0;

    const hasQuestion = explainerAnswer?.question;
    const hasAnswer = explainerAnswer?.answer;

    const tabItems = [
        { key: TabKeys.QUESTION, title: `${t(TabKeys.QUESTION)}`, id: 'Details-tab' },
        { key: TabKeys.KNOWLEDGE, title: `${t(TabKeys.KNOWLEDGE)}`, id: 'Documents-tab' },
        { key: TabKeys.RANKING, title: `${t(TabKeys.RANKING)}`, id: 'Ranking-tab' },
        { key: TabKeys.ANSWER, title: `${t(TabKeys.ANSWER)}`, id: 'Answer-tab' },
    ];

    const renderSelectedTab = () => {
        switch (currentTab) {
            case TabKeys.QUESTION:
                return <ExplainerDetails details={questionDetails} />;
            case TabKeys.KNOWLEDGE:
                return hasRetrievedContexts ? (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col">
                            <div className="font-medium text-lg">Context</div>
                            <div className="text-sm font-normal mt-1">Retrieved parts from the catalog based on your query</div>
                        </div>
                        <ExplainerPartsCatalogTable
                            headers={explainerAnswer.retrieved_contexts!.headers}
                            rows={explainerAnswer.retrieved_contexts!.rows}
                            title="Retrieved Parts Context"
                            description="Initial search results from the parts catalog based on your query"
                        />
                    </div>
                ) : (
                    <div className="text-center text-lg font-medium">No knowledge data available</div>
                );
            case TabKeys.RANKING:
                return hasRerankedContexts ? (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col">
                            <div className="font-medium text-lg">Reranked parts snippet</div>
                            <div className="text-sm font-normal mt-1">Refined and reordered search results based on relevance scoring</div>
                        </div>
                        <ExplainerPartsCatalogTable
                            headers={explainerAnswer.reranked_contexts!.headers}
                            rows={explainerAnswer.reranked_contexts!.rows}
                            title="Reranked Parts Context"
                            description="Refined and reordered search results based on relevance scoring"
                        />
                    </div>
                ) : (
                    <div className="text-center text-lg font-medium">No ranking data available</div>
                );
            case TabKeys.ANSWER:
                return (
                    <div className="flex flex-col h-full overflow-y-auto gap-3">
                        <div className="flex flex-col gap-1">
                            <div className="font-medium text-lg">Advisor generated answer</div>
                            <div className="text-sm font-normal">Answer Generated is based on ranked context</div>
                        </div>
                        <div className='flex-1 overflow-y-auto'>
                            <div className="border-1 p-3 rounded" style={{ backgroundColor: 'transparent' }}>
                                {/* Use API response first, fallback to advisor response if API response is empty or not present */}
                                {(() => {
                                    const apiAnswer = explainerAnswer?.answer;
                                    const answerToDisplay = apiAnswer && apiAnswer.trim() !== '' ? apiAnswer : advisorResponse;
                                    return answerToDisplay || 'No answer available';
                                })()}
                            </div>
                            {explainerAnswer?.cypher_query && (
                                <div className="mt-3">
                                    <div className="font-medium text-sm mb-2">Cypher Query:</div>
                                    <div className="border-1 p-3 rounded bg-default-100 font-mono text-sm">
                                        {explainerAnswer.cypher_query}
                                    </div>
                                </div>
                            )}
                            <div className="text-sm font-normal mt-1">Time taken: {formatTimestamp(currentQuestionAnswerTime)} seconds</div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Modal
            id="parts-catalog-explainer-modal"
            backdrop="blur"
            isOpen={isOpen}
            onClose={() => setViewExplainer(false)}
            className="max-h-[80%] max-w-[90%]"
            isDismissable={false}
            isKeyboardDismissDisabled={true}
        >
            <ModalContent>
                <>
                    <ModalHeader className='flex flex-col'>
                        <h1 className="text-xl font-medium">Parts Catalog Explainer</h1>
                        <div className="text-base font-normal pt-2">
                            View how this parts catalog answer was generated based on the search and ranking process.
                        </div>
                    </ModalHeader>

                    {(explainerAnswer?.retrieved_contexts?.rows && explainerAnswer.retrieved_contexts.rows.length > 0) || (explainerAnswer?.reranked_contexts?.rows && explainerAnswer.reranked_contexts.rows.length > 0) || explainerAnswer?.answer ? (
                        <ModalBody className="overflow-y-auto">
                            <Tabs radius={'sm'} aria-label="Tabs radius" fullWidth size='lg' items={tabItems} selectedKey={currentTab} onSelectionChange={(key) => setCurrentTab(key as TabKeys)}>
                                {(item) => <Tab key={item.key} title={item.title} id={`${item.title}-tab`} />}
                            </Tabs>
                            {renderSelectedTab()}
                        </ModalBody>
                    ) : (
                        <ModalBody className="overflow-y-auto">
                            <div className="text-center text-lg font-medium">
                                Explainer is not available for this answer
                            </div>
                        </ModalBody>
                    )}

                    <ModalFooter>
                        <CtyButton
                            id="parts-catalog-explainer-modal-close-btn"
                            onPress={() => setViewExplainer(false)}
                            variant="bordered"
                            size="lg"
                        >
                            Close
                        </CtyButton>
                    </ModalFooter>
                </>
            </ModalContent>
        </Modal>
    );
};

export default ExplainerPartsCatalogModal; 